import React from "react"
import logo from "../../public/assets/images/ontario_logo.svg"
import { ProgramContainer } from "../entities/formTemplateDataType";
import TrackPageView from "../utilities/TrackPageView";
import { OntarioConfirmDialog } from "./OntarioConfirmDialog";

interface Props {
    programContainer: ProgramContainer;
    isEng: boolean;
    callback;
    callbackProgramContainer?;
}

interface State {
    isEng: boolean;
    showExitDialog: boolean;
    exitDestination: string;
}

export class MCCSSHeaderComponent extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isEng: this.props.isEng,
            showExitDialog: false,
            exitDestination: "",
        };
    }

    componentDidMount() {
        if (this.props.programContainer.EnableExitPopup === "Y") {
            window.addEventListener("beforeunload", this.handleBeforeUnload);
        }
    }

    componentWillUnmount() {
        window.removeEventListener("beforeunload", this.handleBeforeUnload);
    }

    handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (this.props.programContainer.IsDirty) {
            e.preventDefault();
            e.returnValue = "";
        }
    }

    handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        const ontarioUrl = this.props.isEng
            ? "https://www.ontario.ca/page/government-ontario"
            : "https://www.ontario.ca/fr/page/gouvernement-de-lontario";

        if (this.props.programContainer.EnableExitPopup === "Y" && this.props.programContainer.IsDirty) {
            this.setState({ showExitDialog: true, exitDestination: ontarioUrl });
        } else {
            window.location.href = ontarioUrl;
        }
    }

    handleConfirmExit = () => {
        const { exitDestination } = this.state;
        window.removeEventListener("beforeunload", this.handleBeforeUnload);
        this.setState({ showExitDialog: false }, () => {
            if (exitDestination) {
                window.location.href = exitDestination;
            }
        });
    }

    handleCancelExit = () => {
        this.setState({ showExitDialog: false, exitDestination: "" });
    }

    addFrToUrl = () => {
        const enUrlString = window.location.href;
        sessionStorage.setItem("previousUrl", enUrlString);
        const frUrlString = enUrlString.replace("/on-form/", "/on-form/fr/");
        window.history.pushState({}, "", frUrlString);
        sessionStorage.setItem("languageChange", "true");
        TrackPageView(this.props.programContainer);
    };

    removeFrToUrl = () => {
        const url = window.location.href;
        sessionStorage.setItem("previousUrl", url);
        sessionStorage.setItem("languageChange", "true");
        if (url.includes("/fr")) {
            const newUrl = url.replace("/fr", "");
            window.history.pushState(null, "", newUrl);
        }
        TrackPageView(this.props.programContainer);
    };

    generateHeader = () => {
        const ministryName = this.props.isEng
            ? "Ministry of Children, Community and Social Services"
            : "Ministère des Services à l'enfance et des Services sociaux et communautaires";

        const appName = this.props.isEng
            ? this.props.programContainer.Name
            : this.props.programContainer.NameFRA;

        return (
            <div className="documentation-only--application">
                <div className="ontario-header__container">
                    {/* Top row: Ontario logo + language toggle */}
                    <header className="ontario-service-header ontario-header" id="ontario-header">
                        <div className="ontario-row">
                            <div className="ontario-columns ontario-small-6 ontario-service-header__logo">
                                <a
                                    href={this.props.isEng
                                        ? "https://www.ontario.ca/page/government-ontario"
                                        : "https://www.ontario.ca/fr/page/gouvernement-de-lontario"}
                                    onClick={(e) => this.handleLogoClick(e)}
                                >
                                    <img src={logo} alt="Ontario.ca homepage" />
                                </a>
                            </div>
                            <div className="ontario-columns ontario-small-6 ontario-service-header__lang-toggle">
                                {!this.props.programContainer.EnglishOnly && (
                                    <button
                                        className="ontario-header__language-toggler ontario-header-button ontario-header-button--without-outline"
                                        lang={this.props.isEng ? "fr" : "en"}
                                        onClick={() => {
                                            if (this.state.isEng) {
                                                localStorage.setItem("cxp-i18n", "fr");
                                                this.addFrToUrl();
                                            } else {
                                                localStorage.setItem("cxp-i18n", "en");
                                                this.removeFrToUrl();
                                            }
                                            this.setState({ isEng: !this.props.isEng }, () =>
                                                this.props.callback(!this.props.isEng)
                                            );
                                        }}
                                    >
                                        {this.props.isEng ? "Français" : "English"}
                                    </button>
                                )}
                            </div>
                        </div>
                    </header>

                    {/* Sub-header: Ministry name + app name — full-width green background */}
                    <div style={{ backgroundColor: '#1a3629', width: '100%' }}>
                        <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '16px 24px' }}>
                            <div style={{ color: '#ffffff', fontSize: '1.4rem', fontWeight: 700, lineHeight: 1.3 }}>
                                {ministryName}
                            </div>
                            <div style={{ color: '#ffffff', fontSize: '0.95rem', fontWeight: 400, marginTop: '4px', opacity: 0.9 }}>
                                {appName}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        return (
            <>
                {this.generateHeader()}
                <OntarioConfirmDialog
                    visible={this.state.showExitDialog}
                    title={this.props.programContainer.ExitPopupTitle || (this.props.isEng ? "Are you sure you want to exit?" : "Êtes-vous sûr de vouloir quitter?")}
                    message={this.props.programContainer.ExitPopupMessage || (this.props.isEng ? "If you leave this page your progress will not be saved." : "Si vous quittez cette page, votre progression ne sera pas sauvegardée.")}
                    cancelLabel={this.props.programContainer.ExitPopupStayButtonLabel || (this.props.isEng ? "Stay on this page" : "Rester sur cette page")}
                    confirmLabel={this.props.programContainer.ExitPopupExitButtonLabel || (this.props.isEng ? "Exit without saving" : "Quitter sans sauvegarder")}
                    onCancel={this.handleCancelExit}
                    onConfirm={this.handleConfirmExit}
                />
            </>
        );
    }
}
